import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_HIGHWAY_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import type { AppModalProps } from '../apps-data'

type EnableHighwayAppInput = {
  accessToken: string
}

const formSchema = z.object({
  accessToken: z.string().min(1, 'Access token is required')
})

/**
 * This is the modal for the Highway app.
 */
export function HighwayModal({ isOpen, onClose }: AppModalProps) {
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      accessToken: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableHighwayApp, { loading }] = useMutation<
    { enableHighwayApp: { accessToken: string; enableAfterCreation: boolean } },
    EnableHighwayAppInput
  >(ENABLE_HIGHWAY_APP, {
    onCompleted: () => {
      toast.success('Highway app is enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Unauthorized'
          ? 'The Highway access token is invalid.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableHighwayApp({
      variables: {
        accessToken: values.accessToken
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable Highway for Chain</DialogTitle>
          <DialogDescription>
            Enabling this integration requires a Highway account. You will need to add your Highway access token. This
            can be obtained by either having your Chain Implementation Representative obtain it from Highway, or you can
            email Highway support for an access token to be used for Chain.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="accessToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Highway Access Token</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your Highway access token" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/highway"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
